/* KF E1 - desktop pointer-follow stacked product image zoom.
   Event delegation supports gallery image/variant changes without changing Fancybox. */
(() => {
  const productSelector = 'product-info.kf-hover-zoom-enabled';
  const imageSelector = '.yv-product-gallery-slider .gallery-main-item.yv-product-image-item';
  const finePointer = window.matchMedia('(min-width: 1025px) and (hover: hover) and (pointer: fine)');

  function init(root = document) {
    /* Opt in only the stacked gallery, not slider/gallery templates. */
    const candidates = root.matches?.('product-info') ? [root] : root.querySelectorAll('product-info');
    candidates.forEach((product) => {
      if (product.querySelector('.yv-product-gallery-slider:not(.gallery-style)')) {
        product.classList.add('kf-hover-zoom-enabled');
      }
    });
    const products = root.matches?.(productSelector)
      ? [root] : root.querySelectorAll(productSelector);

    products.forEach((product) => {
      if (product.dataset.kfHoverZoomReady === 'true') return;
      product.dataset.kfHoverZoomReady = 'true';
      let active = null;
      let pending = null;
      let raf = 0;

      function reset() {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        pending = null;
        if (active) {
          active.item.classList.remove('kf-hover-zoom-active');
          active.frame.style.removeProperty('--kf-zoom-x');
          active.frame.style.removeProperty('--kf-zoom-y');
          active = null;
        }
      }

      /* KF E1 high-resolution image source: the same original URL used by Fancybox.
         Load only when hovered, never replace the gallery's responsive/lazy image. */
      const retryStates = new WeakMap();
      function ensureHighRes(item, frame) {
        const link = item.querySelector('a[data-thumb][href]');
        const base = frame.querySelector('img:not(.kf-hires-zoom-layer)');
        const src = link?.href || base?.dataset.original;
        if (!src || !base || frame.dataset.kfHiresFailed === src) return;
        let retry = retryStates.get(frame);
        if (!retry || retry.src !== src) {
          if (retry?.timer) clearTimeout(retry.timer);
          retry = { src, failures: 0, timer: null };
          retryStates.set(frame, retry);
        }

        /* Wait for the scheduled retry; mouse movement must not bypass it. */
        if (retry.timer) return;

        let layer = frame.querySelector('.kf-hires-zoom-layer');
        if (layer?.dataset.source === src) return;

        if (layer) layer.remove();
        item.classList.remove('kf-hires-zoom-ready');

        layer = document.createElement('img');
        layer.className = 'kf-hires-zoom-layer';
        layer.alt = '';
        layer.setAttribute('aria-hidden', 'true');
        layer.draggable = false;
        layer.decoding = 'async';
        layer.dataset.source = src;

        /* Match the visible image's fit, focal point and viewport-fit settings. */
        const baseStyle = window.getComputedStyle(base);
        layer.style.objectFit = baseStyle.objectFit;
        layer.style.objectPosition = baseStyle.objectPosition;

        /* Retry this custom hi-res overlay only, never the native gallery. */
        let settled = false;
        function handleFailure() {
          if (settled) return;
          settled = true;
          if (retryStates.get(frame) !== retry) return;
          layer.remove();
          item.classList.remove('kf-hires-zoom-ready');
          retry.failures += 1;
          if (retry.failures > 2) {
            frame.dataset.kfHiresFailed = src;
            return;
          }
          retry.timer = setTimeout(() => {
            retry.timer = null;
            if (finePointer.matches && frame.isConnected &&
                item.isConnected && retryStates.get(frame) === retry) {
              ensureHighRes(item, frame);
            }
          }, retry.failures * 800);
        }

        /* Image load only confirms network completion. Wait for pixel decoding
           before making this layer visible to avoid first-hover white flashes. */
        let readyPending = false;
        async function markReadyAfterDecode() {
          if (readyPending) return;
          readyPending = true;
          try {
            if (typeof layer.decode === 'function') await layer.decode();
            if (layer.isConnected && layer.dataset.source === src &&
                layer.complete && layer.naturalWidth > 0) {
              settled = true;
              retry.failures = 0;
              item.classList.add('kf-hires-zoom-ready');
            }
          } catch (error) {
            handleFailure();
          } finally {
            readyPending = false;
          }
        }
        layer.addEventListener('load', () => { void markReadyAfterDecode(); }, { once: true });
        layer.addEventListener('error', handleFailure, { once: true });

        frame.appendChild(layer);
        layer.src = src;
        if (layer.complete && layer.naturalWidth > 0) {
          void markReadyAfterDecode();
        }
      }

      function paint() {
        raf = 0;
        if (!pending || !finePointer.matches) {
          reset();
          return;
        }
        const { item, frame, x, y } = pending;
        if (!item.isConnected || !frame.isConnected) {
          reset();
          return;
        }
        const rect = frame.getBoundingClientRect();
        if (!rect.width || !rect.height ||
            x < rect.left || x > rect.right ||
            y < rect.top || y > rect.bottom) {
          reset();
          return;
        }
        if (active && active.item !== item) reset();

        const px = Math.max(0, Math.min(100, ((x - rect.left) / rect.width) * 100));
        const py = Math.max(0, Math.min(100, ((y - rect.top) / rect.height) * 100));
        ensureHighRes(item, frame);
        frame.style.setProperty('--kf-zoom-x', px.toFixed(2) + '%');
        frame.style.setProperty('--kf-zoom-y', py.toFixed(2) + '%');
        item.classList.add('kf-hover-zoom-active');
        active = { item, frame };
      }

      /* Preload original-resolution media when a thumbnail is chosen, not
         after the first image hover. Each hidden layer is decoded before use. */
      function preloadImage(item) {
        if (!finePointer.matches || !item || !product.contains(item)) return;
        const frame = item.querySelector('.image-wrapper');
        if (frame?.querySelector('img:not(.kf-hires-zoom-layer)')) {
          ensureHighRes(item, frame);
        }
      }

      function imageForThumb(thumb) {
        const mediaId = thumb?.getAttribute('data-image');
        if (!mediaId) return null;
        return Array.from(product.querySelectorAll(imageSelector))
          .find((item) => item.id === mediaId) || null;
      }

      function preloadFromThumbEvent(event) {
        const thumb = event.target.closest('.gallery-thumbs-item[data-image]');
        if (thumb && product.contains(thumb)) preloadImage(imageForThumb(thumb));
      }

      product.addEventListener('click', preloadFromThumbEvent);
      product.addEventListener('pointerover', (event) => {
        if (event.pointerType === 'mouse') preloadFromThumbEvent(event);
      }, { passive: true });
      product.addEventListener('focusin', preloadFromThumbEvent);

      const selectedThumb = product.querySelector('.gallery-thumbs-item.active[data-image]');
      preloadImage(imageForThumb(selectedThumb) || product.querySelector(imageSelector));

      product.addEventListener('pointermove', (event) => {
        if (!finePointer.matches || event.pointerType !== 'mouse') {
          if (active) reset();
          return;
        }
        const item = event.target.closest(imageSelector);
        const frame = item?.querySelector('.image-wrapper');
        if (!item || !product.contains(item) || !frame?.querySelector('img')) {
          if (active) reset();
          return;
        }
        pending = { item, frame, x: event.clientX, y: event.clientY };
        if (!raf) raf = requestAnimationFrame(paint);
      }, { passive: true });

      product.addEventListener('pointerout', (event) => {
        if (!active) return;
        const item = event.target.closest(imageSelector);
        if (item === active.item && !item.contains(event.relatedTarget)) reset();
      });
      product.addEventListener('pointerleave', reset);
      product.addEventListener('pointercancel', reset);
      product.addEventListener('click', reset, true);
      /* Remove desktop-only hi-res image layers when switching to a mobile
         viewport or a device without mouse hover. Re-create them on next
         desktop hover instead of leaving hidden/duplicate images behind. */
      function cleanupHoverLayersOnResize() {
        if (finePointer.matches) return;
        reset();
        product.querySelectorAll('.kf-hires-zoom-layer').forEach((layer) => layer.remove());
        product.querySelectorAll(imageSelector).forEach((item) => {
          item.classList.remove('kf-hires-zoom-ready', 'kf-hover-zoom-active');
          const frame = item.querySelector('.image-wrapper');
          if (frame) {
            frame.style.removeProperty('--kf-zoom-x');
            frame.style.removeProperty('--kf-zoom-y');
          }
        });
      }
      if (typeof finePointer.addEventListener === 'function') {
        finePointer.addEventListener('change', cleanupHoverLayersOnResize);
      } else if (typeof finePointer.addListener === 'function') {
        finePointer.addListener(cleanupHoverLayersOnResize);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
